import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search, ShoppingCart, Heart, User, Menu, X, ChevronDown, Minus, Plus
} from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { useProductStore } from '../store/productStore';
import { useUserStore } from '../store/userStore';
import { useSettingsStore } from '../store/settingsStore';
import { loadAllProducts } from '../services/firebaseSync';
import logo from '../assets/logo.webp';
import { ProductImage } from './ProductImage';
import { formatPrice } from '../utils/price';

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  const updateActiveDropdownId = (id: string | null) => {
    setActiveDropdownId(id);
    const activeEl = document.activeElement;
    if (activeEl instanceof HTMLElement && activeEl.closest('.dropdown')) {
      activeEl.blur();
    }
  };

  const categories = useProductStore((state) => state.categories);
  const products = useProductStore((state) => state.products);
  const cartItems = useCartStore((state) => state.items);
  const wishlist = useCartStore((state) => state.wishlist);
  const currentUser = useUserStore((state) => state.currentUser);
  const isCartOpen = useCartStore((state) => state.isCartOpen);
  const setIsCartOpen = useCartStore((state) => state.setIsCartOpen);
  const aiBuilderName = useSettingsStore((state) => state.aiBuilderName) || '✨ AI Project Builder';
  const industrialProjectsName = useSettingsStore((state) => state.industrialProjectsName) || '⚙️ Industrial Automation & Projects';

  const searchRef = useRef<HTMLDivElement>(null);

  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  const [animateCart, setAnimateCart] = useState(false);
  const prevCartCountRef = useRef(cartCount);

  // Trigger wiggle animation on cart update
  useEffect(() => {
    if (cartCount > prevCartCountRef.current) {
      setAnimateCart(true);
      const timer = setTimeout(() => setAnimateCart(false), 600);
      return () => clearTimeout(timer);
    }
    prevCartCountRef.current = cartCount;
  }, [cartCount]);

  // Close search suggestions and dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown')) {
        updateActiveDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load all products when search is focused so suggestions have the full database
  useEffect(() => {
    if (isSearchFocused) {
      loadAllProducts();
    }
  }, [isSearchFocused]);

  // Close mobile menu and reset dropdown states on location change
  useEffect(() => {
    setIsOpen(false);
    updateActiveDropdownId(null);
  }, [location]);

  const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, '');
  
  const suggestions = searchQuery.trim().length > 0
    ? products
      .filter(p => {
        const queryNorm = normalize(searchQuery);
        const nameNorm = normalize(p.name || '');
        const descNorm = normalize(p.shortDescription || '');
        return nameNorm.includes(queryNorm) || descNorm.includes(queryNorm);
      })
      .sort((a, b) => {
        const queryNorm = normalize(searchQuery);
        const aNameMatch = normalize(a.name || '').includes(queryNorm);
        const bNameMatch = normalize(b.name || '').includes(queryNorm);
        if (aNameMatch && !bNameMatch) return -1;
        if (!aNameMatch && bNameMatch) return 1;
        return 0;
      })
      .slice(0, 5)
    : [];

  const showSuggestions = isSearchFocused && suggestions.length > 0;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchFocused(false);
      navigate(`/category/all?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSuggestionClick = (productId: string) => {
    setSearchQuery('');
    setIsSearchFocused(false);
    navigate(`/product/${productId}`);
  };



  return (
    <header className="sticky top-0 z-50 w-full no-print">
      {/* Main Navbar */}
      <div className="bg-white/95 backdrop-blur-md shadow-md border-b border-slate-100 py-3.5 px-4 md:px-8">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">

          <div className="flex items-center justify-between w-full md:w-auto">
            <Link to="/" className="flex items-center gap-2.5 group">
              <img src={logo} alt="SensorZone.lk Logo" width="40" height="40" className="h-10 w-auto object-contain rounded-full shadow-sm border border-slate-100 group-hover:scale-105 transition-transform duration-300" />
              <div>
                <span className="font-extrabold text-2xl tracking-tight text-primary">
                  SensorZone<span className="text-secondary">.lk</span>
                </span>
                <span className="block text-[8px] font-bold tracking-widest text-slate-500 uppercase -mt-1">Robotics & IoT Hub</span>
              </div>
            </Link>
            <div className="flex items-center gap-2 md:hidden">
              <button
                onClick={() => setIsCartOpen(true)}
                className="w-11 h-11 flex items-center justify-center text-slate-600 hover:text-primary transition-colors relative focus:outline-none"
                aria-label="Open Cart"
              >
                <div className={`relative transition-transform duration-300 ${animateCart ? 'animate-cart-pop' : ''}`}>
                  <ShoppingCart className="w-6 h-6" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-secondary text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </div>
              </button>
              <button className="w-11 h-11 flex items-center justify-center text-slate-600 hover:text-primary" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle Menu">
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Search bar (with suggestions) */}
          <div className="flex-1 max-w-xl mx-0 md:mx-6" ref={searchRef}>
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="input input-bordered w-full pr-12 rounded-full focus:outline-none focus:ring-2 focus:ring-secondary/40 border-slate-200 shadow-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
              />
              <button
                type="submit"
                aria-label="Search"
                className="absolute right-1 top-1 bottom-1 px-4 bg-primary hover:bg-primary/95 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <Search className="w-4 h-4 text-accent" />
              </button>

              {/* Suggestions Panel */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50">
                  <div className="p-3 bg-slate-50 border-b border-slate-100 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Suggested Products
                  </div>
                  <ul>
                    {suggestions.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors border-b border-slate-100 last:border-0"
                          onClick={() => handleSuggestionClick(p.id)}
                        >
                          <ProductImage productId={p.id} className="w-10 h-10 object-cover rounded-lg border border-slate-100" alt={p.name} />
                          <div>
                            <p className="font-semibold text-sm text-slate-800 line-clamp-1">{p.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-secondary font-bold text-xs">{formatPrice(p.price)}</span>
                              <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${p.stock > 0 ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                                {p.stock > 0 ? 'In Stock' : 'Out of Stock'}
                              </span>
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </form>
          </div>

          {/* Icons Bar */}
          <div className="hidden md:flex items-center gap-5">
            <Link to="/account" className="flex flex-col items-center text-slate-600 hover:text-primary transition-colors group">
              <div className="relative p-1">
                <User className="w-5 h-5 group-hover:scale-105 transition-transform" />
              </div>
              <span className="text-[10px] font-semibold mt-0.5">
                {currentUser && currentUser.name ? `Hi, ${currentUser.name.split(' ')[0]}` : 'Account'}
              </span>
            </Link>

            <Link to="/account?tab=wishlist" className="flex flex-col items-center text-slate-600 hover:text-primary transition-colors group">
              <div className="relative p-1">
                <Heart className="w-5 h-5 group-hover:scale-105 transition-transform" />
                {wishlist.length > 0 && (
                  <span className="absolute top-0 right-0 transform translate-x-1.5 -translate-y-1 bg-red-600 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center animate-bounce">
                    {wishlist.length}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-semibold mt-0.5">Wishlist</span>
            </Link>

            <button
              onClick={() => setIsCartOpen(true)}
              className="flex flex-col items-center text-slate-600 hover:text-primary transition-colors group focus:outline-none"
            >
              <div className={`relative p-1 transition-transform duration-300 ${animateCart ? 'animate-cart-pop' : ''}`}>
                <ShoppingCart className="w-5 h-5 group-hover:scale-105 transition-transform" />
                {cartCount > 0 && (
                  <span className="absolute top-0 right-0 transform translate-x-1.5 -translate-y-1 bg-secondary text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-semibold mt-0.5">Cart</span>
            </button>
          </div>
        </div>

        {/* Categories Link Row (Desktop) & Collapsible Mobile Menu */}
        <div className={`md:flex md:items-center md:justify-center border-t border-slate-100 mt-3 pt-3 ${isOpen ? 'block' : 'hidden'}`}>
          <nav className="flex flex-col md:flex-row items-stretch md:items-center gap-1 md:gap-2">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className={`flex items-center justify-start md:justify-center font-bold px-3.5 py-2 rounded-xl transition-all duration-200 text-xs cursor-pointer ${location.pathname === '/'
                ? 'border border-primary/20 bg-primary/5 text-primary'
                : 'border border-transparent text-slate-700 hover:bg-slate-100/50 hover:text-slate-900'
                }`}
            >
              HOME
            </Link>

            {/* Category Links & Dropdowns */}
            {categories.map((cat) => {
              const isCatActive = location.pathname.startsWith(`/category/${cat.id}`);
              const isDropdownOpen = activeDropdownId === cat.id;
              return (
                <React.Fragment key={cat.id}>
                  {/* Mobile-only Direct Link */}
                  <Link
                    to={`/category/${cat.id}`}
                    onClick={() => setIsOpen(false)}
                    className={`md:hidden flex items-center justify-between font-semibold px-3.5 py-2 rounded-xl transition-all duration-200 text-xs w-full cursor-pointer ${isCatActive
                      ? 'border border-primary/20 bg-primary/5 text-primary'
                      : 'border border-transparent text-slate-700 hover:bg-slate-100/50 hover:text-slate-900'
                      }`}
                  >
                    <span>{cat.name}</span>
                  </Link>

                  {/* Desktop-only Dropdown */}
                  <div
                    className={`hidden md:inline-block dropdown dropdown-bottom ${isDropdownOpen ? 'dropdown-open' : ''}`}
                    onMouseEnter={() => updateActiveDropdownId(cat.id)}
                    onMouseLeave={() => updateActiveDropdownId(null)}
                  >
                    <label
                      tabIndex={0}
                      onClick={() => {
                        updateActiveDropdownId(activeDropdownId === cat.id ? null : cat.id);
                      }}
                      className={`flex items-center justify-center gap-1 font-semibold px-3.5 py-2 rounded-xl transition-all duration-200 text-xs cursor-pointer ${isCatActive
                        ? 'border border-primary/20 bg-primary/5 text-primary'
                        : 'border border-transparent text-slate-700 hover:bg-slate-100/50 hover:text-slate-900'
                        }`}
                    >
                      <span className="flex items-center">
                        {cat.name}
                      </span>
                      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    </label>
                    <ul
                      tabIndex={0}
                      className="dropdown-content menu p-2 shadow-xl bg-white border border-slate-50 rounded-xl w-60 z-50"
                    >
                      <li>
                        <Link
                          to={`/category/${cat.id}`}
                          onClick={() => {
                            setIsOpen(false);
                            updateActiveDropdownId(null);
                            (document.activeElement as HTMLElement)?.blur();
                          }}
                          className="font-bold text-primary hover:bg-slate-50"
                        >
                          View All {cat.name}
                        </Link>
                      </li>
                      <div className="divider my-1"></div>
                      {cat.subCategories.map((sub) => (
                        <li key={sub.id}>
                          <Link
                            to={`/category/${sub.id}`}
                            onClick={() => {
                              setIsOpen(false);
                              updateActiveDropdownId(null);
                              (document.activeElement as HTMLElement)?.blur();
                            }}
                            className="hover:bg-slate-50"
                          >
                            {sub.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </React.Fragment>
              );
            })}

            <Link
              to="/custom-projects"
              onClick={() => setIsOpen(false)}
              className={`flex items-center justify-start md:justify-center font-bold px-3.5 py-2 rounded-xl transition-all duration-200 text-xs cursor-pointer hover-wiggle ${location.pathname === '/custom-projects'
                ? 'border border-primary/20 bg-primary/5 text-primary'
                : 'border border-transparent text-slate-700 hover:bg-slate-100/50 hover:text-slate-900'
                }`}
            >
              {industrialProjectsName}
            </Link>

            <Link
              to="/ai-assistant"
              onClick={() => setIsOpen(false)}
              className={`flex items-center justify-start md:justify-center font-bold px-3.5 py-2 rounded-xl transition-all duration-200 text-xs cursor-pointer ${location.pathname === '/ai-assistant'
                ? 'border border-indigo-500/20 bg-indigo-500/15 text-indigo-700'
                : 'border border-indigo-500/30 bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm hover:from-indigo-600 hover:to-purple-700 hover:scale-105'
                }`}
            >
              {aiBuilderName}
            </Link>

            {/* Mobile-only Nav Icons */}
            <div className="md:hidden flex items-center justify-around border-t border-slate-100 mt-4 pt-4">
              <Link to="/account" onClick={() => setIsOpen(false)} className="flex items-center gap-1 text-slate-600">
                <User className="w-5 h-5" /> {currentUser && currentUser.name ? `Hi, ${currentUser.name.split(' ')[0]}` : 'Account'}
              </Link>
              <Link to="/account?tab=wishlist" onClick={() => setIsOpen(false)} className="flex items-center gap-1 text-slate-600 relative">
                <Heart className="w-5 h-5" /> Wishlist
                {wishlist.length > 0 && <span className="badge badge-error badge-xs ml-1">{wishlist.length}</span>}
              </Link>
              <button
                onClick={() => { setIsOpen(false); setIsCartOpen(true); }}
                className="flex items-center gap-1 text-slate-600 relative focus:outline-none"
              >
                <div className={`flex items-center gap-1 ${animateCart ? 'animate-cart-pop' : ''}`}>
                  <ShoppingCart className="w-5 h-5" /> Cart
                  {cartCount > 0 && <span className="badge badge-secondary badge-xs ml-1">{cartCount}</span>}
                </div>
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Cart Slide-out Drawer */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end no-print font-sans text-xs">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsCartOpen(false)}
          ></div>

          {/* Drawer Panel */}
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col z-10 transition-transform transform duration-300">
            {/* Drawer Header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                <h3 className="font-extrabold text-sm text-slate-800 uppercase tracking-wider">Your Shopping Cart</h3>
                <span className="badge badge-secondary badge-xs font-bold py-1 px-2">{cartCount} items</span>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="text-slate-500 hover:text-slate-800 p-1 focus:outline-none"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Items List */}
            <div className="flex-grow overflow-y-auto p-5 space-y-4">
              {cartItems.length > 0 ? (
                cartItems.map((item) => {
                  const itemPrice = item.selectedVariant ? item.selectedVariant.price : item.product.price;
                  return (
                    <div key={item.id} className="flex gap-3 pb-4 border-b border-slate-100 last:border-0">
                      <ProductImage
                        productId={item.product.id}
                        alt={item.product.name}
                        className="w-14 h-14 object-cover rounded-xl border border-slate-100 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-xs text-slate-800 truncate">{item.product.name}</h4>
                        {item.selectedOptions && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {Object.entries(item.selectedOptions).map(([key, value]) => (
                              <span key={key} className="text-[9px] font-bold text-secondary bg-secondary/5 border border-secondary/15 px-1 py-0.2 rounded">
                                {key}: {value}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-[10px] text-slate-500 font-semibold mt-1">{item.product.categoryId}</p>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-slate-50 text-[10px]">
                            <button
                              onClick={() => useCartStore.getState().updateQuantity(item.id, item.quantity - 1)}
                              className="px-2 py-1.5 hover:bg-slate-100 font-extrabold flex items-center justify-center"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="px-3 py-1 font-bold bg-white text-slate-800 border-x border-slate-200 min-w-[2rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => useCartStore.getState().updateQuantity(item.id, item.quantity + 1)}
                              className="px-2 py-1.5 hover:bg-slate-100 font-extrabold flex items-center justify-center"
                              aria-label="Increase quantity"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="font-extrabold text-xs text-primary">
                            {formatPrice(itemPrice * item.quantity)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => useCartStore.getState().removeItem(item.id)}
                        className="text-red-400 hover:text-red-600 shrink-0 self-start p-1"
                        title="Remove item"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-10 text-slate-500">
                  <ShoppingCart className="w-12 h-12 text-slate-200 mb-3" />
                  <p className="text-xs font-bold">Your cart is empty</p>
                  <p className="text-[10px] text-slate-500 max-w-xs mt-1">Add electronic modules and sensors to get started with your build.</p>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            {cartItems.length > 0 && (
              <div className="p-5 border-t border-slate-100 bg-slate-50">
                <div className="flex justify-between items-center mb-4 text-slate-700">
                  <span className="text-xs font-bold uppercase tracking-wider">Subtotal</span>
                  <span className="font-extrabold text-primary text-base">
                    {formatPrice(cartItems.reduce((acc, item) => {
                      const itemPrice = item.selectedVariant ? item.selectedVariant.price : item.product.price;
                      return acc + itemPrice * item.quantity;
                    }, 0))}
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => { setIsCartOpen(false); navigate('/checkout'); }}
                    className="btn btn-secondary w-full rounded-xl py-3 font-bold uppercase tracking-wider h-auto min-h-0 border-none text-primary"
                  >
                    Checkout Now
                  </button>
                  <button
                    onClick={() => { setIsCartOpen(false); navigate('/cart'); }}
                    className="btn btn-ghost w-full rounded-xl py-3 font-bold uppercase tracking-wider h-auto min-h-0 text-slate-500 hover:bg-slate-200/50"
                  >
                    View Cart Page
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
